import React from 'react';

import type { Section as SectionV3 } from '@wix/ambassador-online-programs-v3-section/types';
import type { Step as StepV3 } from '@wix/ambassador-online-programs-v3-step/types';
import { ChevronRightSmall } from '@wix/wix-ui-icons-common/on-stage';
import {
  ProgressBar,
  Text,
  TextButton,
  TextButtonPriority,
  TextPriority,
  TextTypography,
} from 'wix-ui-tpa/cssVars';
import { useSidebarLayoutBase } from '../../../../views/SidebarLayout/contexts/SidebarLayoutBase/SidebarLayoutBaseContext';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { FCWithChildren } from '@wix/challenges-web-library';
import utils, {
  ChallengeEntityType,
  isStepResolved,
  useNextEntity,
} from '../../../../views/utils';
import { classes, st } from './VideoAutoplayScreen.st.css';

export interface IVideoAutoplayScreenProps {
  className?: string;
  isEnabled: boolean;
  onCancel?(): void;
}

export const VideoAutoplayScreen: FCWithChildren<IVideoAutoplayScreenProps> = ({
  className,
  isEnabled,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const { currentStep, changeAutoplayOptions, checkAutoNavigate } =
    useSidebarLayoutBase();

  const { nextEntity } = useNextEntity(currentStep?.id);
  const nextEntityName =
    nextEntity?.section?.source?.settings?.description?.title ||
    nextEntity?.step?.source?.settings?.general?.description?.title ||
    (nextEntity?.section?.source as SectionV3)?.description?.title ||
    (nextEntity?.step?.source as StepV3)?.description?.title;
  const isStepLocked = utils.isStepLocked(currentStep);
  const isLastEntity = nextEntity.type == ChallengeEntityType.VOID;
  const [progress, setProgress] = React.useState<number>(0);

  const onAutoplayContinue = React.useCallback(async () => {
    const isStepAlreadyResolved = isStepResolved(currentStep);

    if (!isLastEntity) {
      await changeAutoplayOptions(true);
    }

    /*
      In case with not-resolved steps we don't have ability to know when resolve request will be finished,
        so `checkAutoNavigate` function is called in top-level component.
      In case with already resolved videos we can call navigating function immediately.
     */
    if (isStepAlreadyResolved && !isStepLocked) {
      await checkAutoNavigate();
    }
  }, [
    currentStep,
    changeAutoplayOptions,
    isStepLocked,
    checkAutoNavigate,
    isLastEntity,
  ]);

  const onAutoplayCancel = React.useCallback(() => {
    onCancel?.();
  }, [currentStep?.id, onCancel]);

  React.useEffect(() => {
    let int = null;

    if (isEnabled) {
      setProgress(0);

      if (!isLastEntity) {
        int = setInterval(() => {
          setProgress((progress) => {
            const newProgress = progress + 10;

            if (newProgress > 100) {
              clearInterval(int);
              void onAutoplayContinue();
            }

            return newProgress;
          });
        }, 500);
      } else {
        void onAutoplayContinue();
      }
    } else {
      setProgress(0);
    }

    return () => clearInterval(int);
  }, [currentStep?.id, isEnabled]);

  return isEnabled && !isLastEntity ? (
    <div className={st(classes.root, { mobile: isMobile }, className)}>
      <div className={classes.content}>
        <Text
          className={classes.topText}
          priority={TextPriority.primary}
          typography={TextTypography.runningText}
        >
          {t('live-site.step-video.autoplay.top-text')}
        </Text>
        <Text
          className={classes.titleText}
          priority={TextPriority.primary}
          typography={TextTypography.runningText}
        >
          {nextEntityName}
        </Text>
        <ProgressBar
          className={classes.progressBar}
          value={progress}
          min={0}
          max={100}
        />
        <div className={classes.controls}>
          <TextButton
            className={classes.btn}
            prefixIcon={null}
            priority={TextButtonPriority.secondary}
            suffixIcon={null}
            onClick={onAutoplayCancel}
          >
            {t('live-site.step-video.autoplay.cancel')}
          </TextButton>
          <TextButton
            className={classes.btn}
            prefixIcon={null}
            priority={TextButtonPriority.secondary}
            suffixIcon={<ChevronRightSmall />}
            onClick={onAutoplayContinue}
          >
            {t('live-site.step-video.autoplay.continue')}
          </TextButton>
        </div>
      </div>
    </div>
  ) : null;
};
